/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  AuthResult,
  LoginCodeStatusTypes,
  PaymentPeriod,
  PlatformType,
  UserConsentsState,
  UserSubscriptionStatus,
} from "../../../enums";
import {
  IAuthRequestModel,
  IAuthResponseModel,
  IAuthSignUpRequestModel,
  IAuthSignUpResponseModel,
  IAuthViaTokenRequestModel,
  ICreatePaymentOrderRequestModel,
  IErrorModel,
  IOrderStatusModel,
  IPaymentDetailsModel,
  IPaymentInvoiceModel,
  IPaymentOfferModel,
  IPaymentOrderModel,
  ITokenModel,
  IUserConsentModel,
  IUserInfoModel,
  IUserModel,
  IUserSubscriptionModel,
  IValidCouponModel,
  ICreatePaymentDetailsModel,
  ILocaleModel,
  IProductModel,
} from "../../../models";
import { authStorage, sessionManager, HttpFactory } from "../../../services";
import {
  AuthService,
  HttpRequestFulfilledInterceptor,
  HttpRequestRejectedInterceptor,
  HttpResponseFulfilledInterceptor,
  HttpResponseRejectedInterceptor,
  LocalizationService,
  PauseSubscriptionService,
  PaymentsService,
  UserService,
} from "./services";
import { ModelsMapperHelper } from "./helpers";
import { pollUntil } from "@nf/helpers";
import { AppConfig } from "@nf/constants";
import {
  ICancelSubscriptionRequestModel,
  IChangePasswordModel,
  IChangePasswordResponseModel,
  ICreateOrderRequestModel,
  ICreatePaymentDetailsRequestModel,
  ICustomerModel,
  IGetLoginTokenFromCodeModel,
  IGetOffersRequestModel,
  ILoginCodeModel,
  ILoginCodeResponseModel,
  ILoginViaTokenRequestModel,
  IOfferSwitchModel,
  IPauseSubscriptionRequestModel,
  ISubscriptionSwitchRequestModel,
  IUnpauseSubscriptionRequestModel,
  IVerifyLoginCodeResponseModel,
  OrderStatus,
  OrderType,
} from "./models";
import { isActiveSubscriptionStatus, StorageHelper } from "../../../helpers";
import { AuthStore, dispatch } from "../../../store";

const MAX_RETRIES = 3;

export class SkymillDataProvider {
  authService = new AuthService();
  userService = new UserService();
  httpFactory = HttpFactory.getInstance("skymill");
  paymentsService = new PaymentsService();
  pauseSubscriptionService = new PauseSubscriptionService();
  localizationService = new LocalizationService();

  constructor() {
    this.setupSessionManager();
  }

  initHttpFactory(country: string) {
    this.httpFactory.baseUrl = AppConfig.ApiAuthUrl;
    this.httpFactory.addRequestInterceptor(
      "HttpRequestInterceptor",
      (config) => HttpRequestFulfilledInterceptor(config, country),
      HttpRequestRejectedInterceptor
    );
    this.httpFactory.addResponseInterceptor(
      "HttpResponseInterceptor",
      HttpResponseFulfilledInterceptor,
      HttpResponseRejectedInterceptor
    );
  }

  setupSessionManager() {
    sessionManager.on("refresh-success", async (session) => {
      await StorageHelper.setSession(session);
      dispatch(AuthStore.Actions.refreshTokenSuccess(session));
    });

    sessionManager.on("refresh-failure", async () => {
      await StorageHelper.clearUserData();
      await StorageHelper.deleteSession();
      dispatch(AuthStore.Actions.refreshTokenFailure());
    });

    sessionManager?.setRefreshFn(async () => {
      const session = StorageHelper.getSession();
      const refreshToken = session?.RefreshToken;

      if (!refreshToken) {
        throw new Error("No refresh token");
      }

      const response = await this.refreshToken(refreshToken);

      if (!response?.Token) {
        throw new Error("Invalid refresh response");
      }

      const newSession = {
        ...session,
        Token: response.Token,
      };

      StorageHelper.setSession(session);

      return newSession;
    });
  }

  async signIn(data: IAuthRequestModel): Promise<IAuthResponseModel> {
    const deviceType =
      data.Device?.PlatformCode === PlatformType.Tizen ||
      data.Device?.PlatformCode === PlatformType.TitanOS ||
      data.Device?.PlatformCode === PlatformType.WebOS
        ? PlatformType.SmartTV
        : data.Device?.PlatformCode;

    const payload = {
      email: data.Username || "",
      password: data.Password || "",
      device_id: data.Device?.Id || "",
      device_name: data.Device?.Name,
      device_type: deviceType,
    };

    const response = await this.authService.login(payload);
    const authResponse = ModelsMapperHelper.toAuthResponseModel(response);
    StorageHelper.setSession(authResponse.AuthorizationToken);

    const customer = await this.authService.customer();
    const user = ModelsMapperHelper.toUserInfoModel(customer);

    return { ...authResponse, User: user };
  }

  async signUp(
    data: IAuthSignUpRequestModel
  ): Promise<IAuthSignUpResponseModel> {
    try {
      const response = await this.authService.signup({
        email: data.Email,
        utm_source: data.UtmSource,
        utm_medium: data.UtmMedium,
        utm_campaign: data.UtmCampaign,
      });

      return ModelsMapperHelper.toAuthSignUpResponseModel(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async validatePassword(password: string, email?: string): Promise<boolean> {
    try {
      const device = await StorageHelper.getDevice();
      const payload = {
        email: email || "",
        password: password,
        device_id: device.Id,
      };
      const response = await this.authService.login(payload);
      const result = ModelsMapperHelper.toAuthResponseModel(response);

      StorageHelper.setSession(result.AuthorizationToken);

      return true;
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async loginViaToken(
    data: IAuthViaTokenRequestModel
  ): Promise<IAuthResponseModel> {
    const deviceType =
      data.Device?.PlatformCode === PlatformType.Tizen ||
      data.Device?.PlatformCode === PlatformType.TitanOS ||
      data.Device?.PlatformCode === PlatformType.WebOS
        ? PlatformType.SmartTV
        : data.Device?.PlatformCode;

    const payload: ILoginViaTokenRequestModel = {
      loginToken: data.LoginToken,
      device_id: data.Device?.Id || "",
      device_name: data.Device?.Name,
      device_type: deviceType || "WEB",
    };

    const response = await this.authService.loginViaToken(payload);
    return ModelsMapperHelper.toAuthResponseModel(response);
  }

  async signInViaToken(
    data: IAuthViaTokenRequestModel
  ): Promise<IAuthResponseModel> {
    const result = await this.loginViaToken(data);
    StorageHelper.setSession(result.AuthorizationToken);

    const customer = await this.authService.customer();
    const user = ModelsMapperHelper.toUserInfoModel(customer);

    return { ...result, User: user };
  }

  async getCustomer(): Promise<IUserInfoModel> {
    try {
      const response = await this.authService.customer();
      return ModelsMapperHelper.toUserInfoModel(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async signOut(refreshToken: string): Promise<void> {
    try {
      const payload = {
        refresh_token: refreshToken,
      };
      await this.authService.logout(payload);
      authStorage.deleteSession();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  public async refreshToken(refreshToken: string): Promise<ITokenModel> {
    try {
      const response = await this.authService.refreshToken({
        refresh_token: refreshToken,
      });
      const token: ITokenModel = {
        AuthResult: AuthResult.OK,
        Token: response.idToken,
      };
      return token;
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getProfile(): Promise<IUserModel> {
    try {
      const response = await this.userService.profile();
      return ModelsMapperHelper.toUserModel(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async updateProfile(data: Partial<IUserModel>): Promise<IUserModel> {
    try {
      const payload: Partial<ICustomerModel> = {
        first_name: data.FirstName,
        last_name: data.LastName,
        phone_prefix: data.PhoneNumberPrefix,
        phone_number: data.PhoneNumberNumber,
      };

      const response = await Promise.all([
        this.userService.updateProfile(payload),
        data.Email && this.userService.updateEmail(data.Email),
      ]);

      const user = ModelsMapperHelper.toUserModel({
        ...response[1],
        ...response[0],
      });

      return Promise.resolve(user);
    } catch (error: any) {
      if (error?.message && error.message.includes("Request throttled"))
        error.message = "request_throttled";

      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getUserConsents(): Promise<IUserConsentModel[]> {
    try {
      const response = await Promise.all([
        this.userService.availableConsents(),
        this.userService.consents(),
      ]);

      return ModelsMapperHelper.toConsentModel(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async updateUserConsents(
    data: IUserConsentModel[]
  ): Promise<IUserConsentModel[]> {
    const payload = {
      consents: data.map((consent) => ({
        name: consent.ConsentName,
        state: consent.Accepted
          ? UserConsentsState.Accepted
          : UserConsentsState.Declined,
        version: consent.ConsentVersion,
      })),
    };

    await this.userService.updateUserConsents(payload);
    return data;
  }

  async changePassword(
    data: IChangePasswordModel
  ): Promise<IChangePasswordResponseModel> {
    try {
      return await this.authService.changePassword(data);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getUserSubscriptions(): Promise<IUserSubscriptionModel[]> {
    try {
      const { subscriptions } = await this.userService.subscriptions();
      return subscriptions.map((subscription) =>
        ModelsMapperHelper.toSubscriptionModel(subscription)
      );
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async cancelUserSubscription(data: ICancelSubscriptionRequestModel) {
    try {
      const response = await this.userService.cancelSubscription(data);

      await pollUntil({
        fetch: async () => {
          const orderStatus = await this.paymentsService.getOrderStatus(
            response.order_id
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          if (orderStatus.order.type === OrderType.CANCEL_SUB_IN_SWITCH) {
            return (
              orderStatus.order.status === OrderStatus.COMPLETED &&
              orderStatus.subscription?.status ===
                UserSubscriptionStatus.TERMINATED
            );
          }

          return (
            (orderStatus.order.status === OrderStatus.COMPLETED &&
              orderStatus.subscription?.status ===
                UserSubscriptionStatus.CANCELLED) ||
            orderStatus.subscription?.status ===
              UserSubscriptionStatus.TERMINATED // `in_dunning` subscription returns draft + terminated
          );
        },
        maxRetries: MAX_RETRIES,
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async uncancelUserSubscription(id: number) {
    try {
      const response = await this.userService.uncancelSubscription(id);

      await pollUntil({
        fetch: async () => {
          const orderStatus = await this.paymentsService.getOrderStatus(
            response.order_id
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          return (
            orderStatus.order.status === OrderStatus.COMPLETED &&
            isActiveSubscriptionStatus(orderStatus.subscription?.status)
          );
        },
        maxRetries: MAX_RETRIES,
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async pauseUserSubscription(data: IPauseSubscriptionRequestModel) {
    try {
      const response = await this.pauseSubscriptionService.pauseSubscription(
        data
      );

      await pollUntil({
        fetch: async () => {
          const orderStatus = await this.paymentsService.getOrderStatus(
            response.order_id
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          return (
            orderStatus.order.type === OrderType.PAUSE_SUB &&
            orderStatus.order.status === OrderStatus.COMPLETED
          );
        },
      });

      return Promise.resolve({ Message: "SUBSCRIPTION_PAUSED" });
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async unpauseUserSubscription(data: IUnpauseSubscriptionRequestModel) {
    try {
      const response = await this.pauseSubscriptionService.unpauseSubscription(
        data
      );

      await pollUntil({
        fetch: async () => {
          const orderStatus = await this.paymentsService.getOrderStatus(
            response.order_id
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          return (
            orderStatus.order.type === OrderType.UNPAUSE_SUB &&
            orderStatus.order.status === OrderStatus.COMPLETED
          );
        },
      });

      return Promise.resolve({ Message: "SUBSCRIPTION_UNPAUSED" });
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getOffers(
    data?: IGetOffersRequestModel
  ): Promise<IPaymentOfferModel[]> {
    const { offers } = await this.paymentsService.getOffers(data);

    const monthlyOffer = offers.find(
      (offer) => offer.period === PaymentPeriod.Month
    );
    const monthlyPrice = monthlyOffer?.price ? +monthlyOffer.price : 0;
    const annualBaselinePrice = monthlyPrice * 12;

    const mappedOffers = offers.map((offer) => {
      const paymentOffer = ModelsMapperHelper.toPaymentOfferModel(offer);

      if (
        paymentOffer.Period === PaymentPeriod.Year &&
        !paymentOffer.SavingsPrice
      ) {
        paymentOffer.SavingsPrice = annualBaselinePrice;
      }

      return paymentOffer;
    });

    return [
      ...mappedOffers.filter((offer) => offer.Period === PaymentPeriod.Month),
      ...mappedOffers.filter((offer) => offer.Period === PaymentPeriod.Year),
    ];
  }

  async getProducts(): Promise<IProductModel[]> {
    try {
      const response = await this.paymentsService.getProducts();
      const products = response.products.map((product) =>
        ModelsMapperHelper.toProductModel(product)
      );

      return Promise.resolve([
        ...products.filter((product) => product.Period === PaymentPeriod.Month),
        ...products.filter((product) => product.Period === PaymentPeriod.Year),
      ]);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async createPaymentDetails(
    data: ICreatePaymentDetailsModel
  ): Promise<IPaymentOrderModel> {
    const payload: ICreatePaymentDetailsRequestModel = {
      accept_url: data.AcceptUrl,
      cancel_url: data.CancelUrl,
    };

    try {
      const response = await this.paymentsService.createPaymentDetails(payload);

      return ModelsMapperHelper.toPaymentOrderModel(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async createPaymentOrder(
    data: ICreatePaymentOrderRequestModel
  ): Promise<IPaymentOrderModel> {
    const payload: ICreateOrderRequestModel = {
      offer_id: data.OfferId,
      coupon_code: data.CouponCode,
      offer_handle: data.OfferHandle,
      accept_url: data.AcceptUrl,
      cancel_url: data.CancelUrl,
    };

    try {
      const response = await this.paymentsService.createOrder(payload);
      return ModelsMapperHelper.toPaymentOrderModel(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getPaymentInvoices(): Promise<IPaymentInvoiceModel[]> {
    const response = await this.paymentsService.paymentInvoices();
    return response.invoices.map((invoice) =>
      ModelsMapperHelper.toInvoiceModel(invoice)
    );
  }

  async getInvoicePDF(invoiceId: string): Promise<string> {
    try {
      return await this.paymentsService.getInvoicePDF(invoiceId);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getPaymentDetails(): Promise<IPaymentDetailsModel | undefined> {
    try {
      const response = await this.paymentsService.getPaymentDetails();
      const payment = response?.[0];

      if (payment) {
        return Promise.resolve(
          ModelsMapperHelper.toPaymentDetailsModel(payment)
        );
      }

      return;
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async validateCoupon(coupon: string): Promise<IValidCouponModel> {
    try {
      const response = await this.paymentsService.validateCoupon(coupon);
      return ModelsMapperHelper.toValidCouponModel(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getOrderStatus(orderId: string): Promise<IOrderStatusModel> {
    try {
      const response = await this.paymentsService.getOrderStatus(orderId);
      return ModelsMapperHelper.toOrderStatusModel(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getLoginCode(): Promise<ILoginCodeResponseModel> {
    try {
      return await this.authService.getLoginCode();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async verifyLoginCode(
    data: ILoginCodeModel
  ): Promise<IVerifyLoginCodeResponseModel> {
    try {
      const payload = {
        code: data.code,
        refresh_token: data.refresh_token,
      };
      const response = await this.authService.verifyCode(payload);

      if (response.status === LoginCodeStatusTypes.expired) {
        return Promise.reject(ModelsMapperHelper.toErrorModel(response.status));
      }

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getLoginTokenFromCode(
    data: ILoginCodeModel
  ): Promise<IGetLoginTokenFromCodeModel> {
    try {
      const payload = {
        code: data.code,
      };
      const response = await this.authService.getLoginTokenFromCode(payload);

      return response;
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getOfferSwitches(): Promise<IOfferSwitchModel[]> {
    try {
      const response = await this.userService.offerSwitches();
      const offerSwitches = response.map((offerSwitch) =>
        ModelsMapperHelper.toOfferSwitchModel(offerSwitch)
      );

      return Promise.resolve(offerSwitches);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async switchUserSubscription(
    data: ISubscriptionSwitchRequestModel
  ): Promise<void> {
    try {
      const response = await this.userService.switchSubscription(data);

      await pollUntil({
        fetch: async () => {
          const orderStatus = await this.paymentsService.getOrderStatus(
            response.order_id
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          return (
            orderStatus.order.status === OrderStatus.COMPLETED &&
            orderStatus.subscription?.status ===
              UserSubscriptionStatus.PENDING_SWITCH
          );
        },
        maxRetries: MAX_RETRIES,
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async cancelUserSubscriptionSwitch(): Promise<void> {
    try {
      const response = await this.userService.cancelSubscriptionSwitch();

      await pollUntil({
        fetch: async () => {
          const orderStatus = await this.paymentsService.getOrderStatus(
            response.order_id
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          return (
            orderStatus.order.status === OrderStatus.COMPLETED &&
            orderStatus.subscription?.status ===
              UserSubscriptionStatus.TERMINATED
          );
        },
        maxRetries: MAX_RETRIES,
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getUserCountry(): Promise<string | undefined> {
    try {
      const response = await this.localizationService.locales();
      return response.cloudfront_viewer_country?.code;
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getLocales(): Promise<ILocaleModel[]> {
    try {
      const response = await this.localizationService.locales();
      return response.locales.map(ModelsMapperHelper.toLocaleModel);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }
}
